import { Inject, Injectable } from '@angular/core';
import { CustomErrorHandler } from './custom.error-handler';
import { Router } from '@angular/router';
import { AngularPlugin } from '@microsoft/applicationinsights-angularplugin-js';
import { ApplicationInsights, IExceptionTelemetry, ITelemetryItem } from '@microsoft/applicationinsights-web';
import { LOG_CONFIG, LoggingConfig } from '@config/log-config.types';

@Injectable({
  providedIn: 'root',
})
export class ApplicationInsightsService {
  private readonly angularPlugin = new AngularPlugin();
  private readonly appInsights = new ApplicationInsights({
    config: {
      instrumentationKey: this.loggingConfig.ApplicationInsights.InstrumentationKey,
      extensions: [this.angularPlugin],
      extensionConfig: {
        [this.angularPlugin.identifier]: {
          router: this.router,
          errorServices: [new CustomErrorHandler()],
        },
      },
    },
  });
  constructor(
    private readonly router: Router,
    @Inject(LOG_CONFIG) private readonly loggingConfig: LoggingConfig,
  ) {
    this.initializeApplicationInsights();
  }

  private initializeApplicationInsights(): void {
    if (!this.hasInstrumentationKey()) {
      return;
    }

    this.appInsights.loadAppInsights();
    this.appInsights.addTelemetryInitializer(this.createTelemetryInitializer());
  }

  private hasInstrumentationKey(): boolean {
    return !!this.loggingConfig.ApplicationInsights.InstrumentationKey;
  }

  private createTelemetryInitializer(): (envelope: ITelemetryItem) => void {
    return (envelope: ITelemetryItem) => {
      if (envelope.tags) {
        this.enrichTelemetryTags(envelope.tags);
      }
    };
  }

  private enrichTelemetryTags(tags: { [key: string]: string }): void {
    tags['Application'] = this.loggingConfig.ApplicationInsights.ApplicationName;
    tags['ai.cloud.role'] = this.loggingConfig.ApplicationInsights.CloudRoleName;
  }
  // expose methods that can be used in components and services
  trackEvent(name: string, properties?: { [key: string]: unknown }): void {
    this.appInsights.trackEvent({ name }, properties);
  }
  trackTrace(message: string): void {
    this.appInsights.trackTrace({ message });
  }
  trackException(error: IExceptionTelemetry) {
    this.appInsights.trackException(error);
  }
}

